import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Theme,
} from "@mui/material";
import { motion } from "framer-motion";
import { HeatSeries } from "src/store/api/generatedApi";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

const monoCellSx = (theme: Theme) => ({
  fontFamily: theme.typography.body1Mono.fontFamily,
});

interface Props {
  heatData: HeatSeries;
}

export const HeatInformationTable = ({ heatData }: Props) => {
  const { t } = useTranslation();
  const { t: tenantT } = useTenantTranslation();
  const units = useUnitsFormatter(false);
  const { toLocalDatetime } = useLocalDatetime();
  const { format } = useNumberSerialiser();

  const summaryData = heatData.summary_data;

  const sortedSummaryData = useMemo(
    () => [...summaryData].sort((a, b) => a.name.localeCompare(b.name)),
    [summaryData]
  );

  return (
    <Stack>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.1 }}
      >
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>{tenantT("heat")}</TableCell>
                <TableCell sx={monoCellSx}>{heatData.heat_id}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tenantT("steelGrade")}</TableCell>
                <TableCell>{heatData.steel_grade}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tenantT("sequenceShort")}</TableCell>
                <TableCell sx={monoCellSx}>
                  {heatData.sequence_number}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tenantT("startTime")}</TableCell>
                <TableCell sx={monoCellSx}>
                  {heatData.start_timestamp
                    ? toLocalDatetime(heatData.start_timestamp)
                    : "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tenantT("tapTime")}</TableCell>
                <TableCell sx={monoCellSx}>
                  {heatData.tap_timestamp
                    ? toLocalDatetime(heatData.tap_timestamp)
                    : "-"}
                </TableCell>
              </TableRow>
              {sortedSummaryData.map((summary) =>
                summary.value === null ? null : (
                  <TableRow key={summary.name}>
                    <TableCell>{t(summary.name)}</TableCell>
                    <TableCell sx={monoCellSx}>
                      {format(summary.value, {
                        decimalPlaces: summary.decimal_places ?? undefined,
                      })}{" "}
                      {summary.unit_type === null
                        ? ""
                        : units(summary.unit_type, false)}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </motion.div>
    </Stack>
  );
};
